/**
 * PURPOSE: Administrar el sorteo de países del evento 17.
 * RESPONSIBILITIES: Mostrar posiciones, sortear países, guardar y restaurar estaciones.
 * COLLABORATORS: event17CountryDraw service y ConfirmUndoCountryDrawModal.
 * ROUTE: frontend/src/area/SorteoArea/SorteoArea2.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaArrowRotateLeft,
  FaFloppyDisk,
  FaShuffle,
} from "react-icons/fa6";
import {
  getEvent17CountryDraw,
  resetEvent17CountryDraw,
  updateEvent17CountryDraw,
} from "../../services/event17CountryDraw.js";
import ConfirmUndoCountryDrawModal from "./components/ConfirmUndoCountryDrawModal.jsx";

const DEFAULT_LOGO_URL =
  "https://storage.googleapis.com/la_liga_files/fedup/images/logo_primax.png";

const fisherYatesShuffle = (items) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const Spinner = ({ className = "h-4 w-4" }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4l4-4-4-4v4a12 12 0 00-12 12h4z"
    />
  </svg>
);

const compactButtonBase =
  "inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

const SorteoArea2 = () => {
  const mountedRef = useRef(true);
  const [slots, setSlots] = useState([]);
  const [countries, setCountries] = useState([]);
  const [excludedCountries, setExcludedCountries] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState("");

  const applyServerState = (data) => {
    setSlots(Array.isArray(data?.slots) ? data.slots : []);
    setCountries(Array.isArray(data?.countries) ? data.countries : []);
    setExcludedCountries(
      Array.isArray(data?.excludedCountries) ? data.excludedCountries : []
    );
    setIsDirty(false);
  };

  const loadDraw = async () => {
    setLoadingInitial(true);
    setError("");

    try {
      const data = await getEvent17CountryDraw();
      if (!mountedRef.current) return;
      applyServerState(data);
    } catch (requestError) {
      console.error("Error loading event 17 country draw:", requestError);
      if (!mountedRef.current) return;
      setError(
        requestError.response?.data?.msg ||
          "No se pudo cargar la configuración del sorteo."
      );
    } finally {
      if (mountedRef.current) setLoadingInitial(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadDraw();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const assignedCountryCodes = useMemo(
    () =>
      new Set(
        slots
          .map((slot) => slot?.country?.code)
          .filter((countryCode) => Boolean(countryCode))
      ),
    [slots]
  );

  const excludedCountryCodes = useMemo(
    () => new Set(excludedCountries.map((country) => country.code)),
    [excludedCountries]
  );

  const slotCount = slots.length;
  const countryCount = countries.length;
  const excludedCount = Math.max(0, countryCount - slotCount);
  const hasCompleteLocalDraw =
    slotCount > 0 && slots.every((slot) => Boolean(slot?.country?.code));

  const handleSorteo = () => {
    if (slotCount === 0 || countryCount <= slotCount) {
      setError("La configuración del sorteo está incompleta.");
      return;
    }

    const shuffledCountries = fisherYatesShuffle(countries);
    const selectedCountries = shuffledCountries.slice(0, slotCount);
    const notSelectedCountries = shuffledCountries.slice(slotCount);

    setSlots(
      slots.map((slot, index) => ({
        ...slot,
        country: selectedCountries[index],
        logoUrl: selectedCountries[index].imageUrl,
      }))
    );
    setExcludedCountries(notSelectedCountries);
    setIsDirty(true);
    setError("");
  };

  const handleGuardar = async () => {
    if (!isDirty || loadingSave || loadingReset) return;

    if (!hasCompleteLocalDraw) {
      toast.error("Primero realiza el sorteo.");
      return;
    }

    setLoadingSave(true);
    setError("");

    try {
      const data = await updateEvent17CountryDraw(
        slots.map((slot) => ({
          idinstitution: Number(slot.idinstitution),
          countryCode: slot.country.code,
        }))
      );

      if (!mountedRef.current) return;
      applyServerState(data);
      toast.success("Cambios guardados");
    } catch (requestError) {
      console.error("Error saving event 17 country draw:", requestError);
      if (!mountedRef.current) return;
      const message =
        requestError.response?.data?.msg || "No se pudo guardar el sorteo.";
      setError(message);
      toast.error(message);
    } finally {
      if (mountedRef.current) setLoadingSave(false);
    }
  };

  const handleReset = async () => {
    setLoadingReset(true);
    setError("");

    try {
      const data = await resetEvent17CountryDraw();
      if (!mountedRef.current) return;
      applyServerState(data);
      setUndoOpen(false);
      toast.success("Sorteo deshecho");
    } catch (requestError) {
      console.error("Error resetting event 17 country draw:", requestError);
      if (!mountedRef.current) return;
      const message =
        requestError.response?.data?.msg || "No se pudo deshacer el sorteo.";
      setError(message);
      toast.error(message);
    } finally {
      if (mountedRef.current) setLoadingReset(false);
    }
  };

  if (loadingInitial) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-2xl bg-slate-50 text-slate-600">
        <div className="flex items-center gap-3 text-sm font-semibold">
          <Spinner className="h-5 w-5" />
          Cargando sorteo...
        </div>
      </div>
    );
  }

  if (error && slots.length === 0) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl bg-slate-50 p-6 text-center">
        <p className="font-semibold text-red-700">{error}</p>
        <button
          type="button"
          onClick={loadDraw}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const busy = loadingSave || loadingReset;

  return (
    <div className="rounded-2xl bg-slate-50 p-4 sm:p-6">
      <header className="mb-6 rounded-2xl bg-white px-5 py-5 shadow-sm ring-1 ring-slate-200">
        <h2 className="text-2xl font-bold text-slate-900">Sorteo de países</h2>
        <p className="mt-1 text-sm text-slate-500">
          {slotCount} estaciones · {countryCount} países
        </p>
      </header>

      {error && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      <section className="mb-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <h3 className="text-base font-bold text-slate-900">Países</h3>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7">
          {countries.map((country) => {
            const isAssigned = assignedCountryCodes.has(country.code);
            const isExcluded = excludedCountryCodes.has(country.code);

            return (
              <div
                key={country.code}
                className={`rounded-xl border px-3 py-3 transition ${
                  isExcluded
                    ? "border-amber-200 bg-amber-50"
                    : isAssigned
                    ? "border-blue-100 bg-blue-50/50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex h-10 items-center justify-center">
                  <img
                    src={country.imageUrl}
                    alt={`Bandera de ${country.name}`}
                    className="max-h-8 w-full object-contain"
                  />
                </div>
                <p className="mt-2 truncate text-center text-xs font-semibold text-slate-700">
                  {country.name}
                </p>
                {(isAssigned || isExcluded) && (
                  <p
                    className={`mt-1 text-center text-[10px] font-semibold ${
                      isExcluded ? "text-amber-700" : "text-blue-600"
                    }`}
                  >
                    {isExcluded ? "No seleccionado" : "Asignado"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-slate-900">
              Asignación por posición
            </h3>
            {isDirty && (
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                Sin guardar
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSorteo}
              disabled={busy}
              className={`${compactButtonBase} border border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700 focus:ring-blue-200`}
            >
              <FaShuffle className="text-xs" />
              {hasCompleteLocalDraw ? "Volver a sortear" : "Sortear"}
            </button>

            <button
              type="button"
              onClick={handleGuardar}
              disabled={!isDirty || busy}
              className={`${compactButtonBase} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-200`}
            >
              {loadingSave ? (
                <Spinner />
              ) : (
                <FaFloppyDisk className="text-xs" />
              )}
              {loadingSave ? "Guardando..." : "Guardar"}
            </button>

            <button
              type="button"
              onClick={() => setUndoOpen(true)}
              disabled={busy}
              className={`${compactButtonBase} border border-red-200 bg-white text-red-600 hover:bg-red-50 focus:ring-red-100`}
            >
              <FaArrowRotateLeft className="text-xs" />
              Deshacer
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {slots.map((slot) => {
            const country = slot.country;
            const imageUrl =
              country?.imageUrl || slot.logoUrl || DEFAULT_LOGO_URL;
            const stationName = slot.abrev || slot.position;

            return (
              <article
                key={slot.idinstitution}
                className="flex min-h-[112px] items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-200 hover:shadow-md"
              >
                <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-50 p-2 ring-1 ring-slate-100">
                  <img
                    src={imageUrl}
                    alt={country ? `Bandera de ${country.name}` : stationName}
                    className="max-h-12 w-full object-contain"
                  />
                </div>

                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-slate-900">
                    {stationName}
                  </p>
                  <p
                    className={`mt-1 truncate text-sm font-medium ${
                      country ? "text-blue-700" : "text-slate-400"
                    }`}
                  >
                    {country?.name || "Sin asignar"}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
        <h3 className="text-sm font-bold text-amber-950">
          {excludedCount === 1
            ? "País no seleccionado"
            : "Países no seleccionados"}
        </h3>

        {excludedCountries.length === excludedCount && excludedCount > 0 ? (
          <div className="mt-3 flex flex-wrap gap-3">
            {excludedCountries.map((country) => (
              <div
                key={country.code}
                className="inline-flex items-center gap-3 rounded-xl bg-white px-3 py-2 ring-1 ring-amber-200"
              >
                <img
                  src={country.imageUrl}
                  alt={`Bandera de ${country.name}`}
                  className="h-7 w-10 object-contain"
                />
                <span className="text-sm font-semibold text-slate-700">
                  {country.name}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-amber-800">Aún no hay sorteo.</p>
        )}
      </section>

      <ConfirmUndoCountryDrawModal
        open={undoOpen}
        loading={loadingReset}
        onCancel={() => setUndoOpen(false)}
        onConfirm={handleReset}
      />
    </div>
  );
};

export default SorteoArea2;
